import { AdSize, SizeMappingEntry } from './gpt';

/**
 * Central configuration for web display ads (Google Ad Manager / GPT).
 *
 * Runtime on/off gating lives in feature flags (see
 * Webapp/shared/concepts/feature-flags.ts, ADS group) which are controlled
 * without a deploy via the dynconfig-published featureFlags.json fetched from
 * FLAP_STATIC_URL on every request. The values below (network code, ad unit
 * paths, sizes) are build-time constants with the agreed GAM defaults;
 * changing a path requires a deploy.
 */

export const GAM_NETWORK_CODE = '21709104563';

export const AD_UNIT_PATHS = {
  leaderboard: `/${GAM_NETWORK_CODE}/web/leaderboard`,
  infeed: `/${GAM_NETWORK_CODE}/web/infeed`,
  articleRail: `/${GAM_NETWORK_CODE}/web/article_rail`,
};

// Leaderboard: 728x90 on desktop/tablet, 320x50 on narrow viewports. The base
// size list is passed to defineSlot; the mapping is what GPT actually uses per
// viewport.
//
// 970x90 was dropped 2026-09-02. A GAM ad-unit report (impressions by rendered
// creative size) showed 970x90 delivers ~0 fill on web (5 impressions in 7 days
// across ALL inventory) while 728x90 is the size that actually fills on desktop
// (~100/wk) and the 320x50 family carries mobile-web. Requesting 970x90 at
// desktop width was dead weight and, offered alongside 728x90, appeared to
// suppress the 728x90 fill that works when it's the only desktop size (observed:
// the leaderboard rendered only in the 768-1023 band, never at >=1024).
export const LEADERBOARD_SIZES: AdSize[] = [
  [728, 90],
  [320, 50],
];
export const LEADERBOARD_SIZE_MAPPING: SizeMappingEntry[] = [
  { viewport: [1024, 0], sizes: [[728, 90]] },
  { viewport: [768, 0], sizes: [[728, 90]] },
  { viewport: [0, 0], sizes: [[320, 50]] },
];

export const MEDIUM_RECTANGLE_SIZES: AdSize[] = [[300, 250]];

export const DEFAULT_INFEED_AD_INTERVAL = 9;

type FeatureFlags = Partial<Record<string, boolean>>;

export const isWebAdsEnabled = (featureFlags?: FeatureFlags): boolean =>
  !!featureFlags?.WEB_ADS;

export const isLeaderboardAdEnabled = (featureFlags?: FeatureFlags): boolean =>
  isWebAdsEnabled(featureFlags) && !!featureFlags?.WEB_ADS_LEADERBOARD;

export const isInfeedAdEnabled = (featureFlags?: FeatureFlags): boolean =>
  isWebAdsEnabled(featureFlags) && !!featureFlags?.WEB_ADS_INFEED;

export const isArticleRailAdEnabled = (featureFlags?: FeatureFlags): boolean =>
  isWebAdsEnabled(featureFlags) && !!featureFlags?.WEB_ADS_ARTICLE_RAIL;

/**
 * flint (in-house server-side header bidding) pre-bid on display slots:
 * when active, slots query flint before GPT and forward the winning bid as
 * GAM key-values (see ./flint.ts).
 */
export const isFlintHeaderBiddingEnabled = (
  featureFlags?: FeatureFlags,
): boolean => isWebAdsEnabled(featureFlags) && !!featureFlags?.WEB_ADS_FLINT;

/**
 * Ads identity, phase 1 (see ./identity.ts): GPT PPID + LiveRamp RampID
 * envelope for logged-in users, consent permitting.
 */
export const isAdsIdentityEnabled = (featureFlags?: FeatureFlags): boolean =>
  isWebAdsEnabled(featureFlags) && !!featureFlags?.WEB_ADS_IDENTITY;

/**
 * In-feed insertion interval ("an ad card every N story tiles"). The feature
 * flag mechanism is boolean-only, so the configurable interval is expressed
 * as discrete override flags; with none active the default (9) applies.
 */
export const getInfeedAdInterval = (featureFlags?: FeatureFlags): number => {
  if (featureFlags?.WEB_ADS_INFEED_INTERVAL_3) {
    return 3;
  }
  if (featureFlags?.WEB_ADS_INFEED_INTERVAL_6) {
    return 6;
  }
  if (featureFlags?.WEB_ADS_INFEED_INTERVAL_12) {
    return 12;
  }
  return DEFAULT_INFEED_AD_INTERVAL;
};
